/**
 * Custom StackBarChartComponent - Override Component
 * Auto generated overrides from shared library
 */
import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { StackBarChartComponent as ParentStackBarChartComponent } from '@shared-lib/chart-components/stack-bar-chart/stack-bar-chart.component';
import { applyRoleVisibilityToOption } from '@shared-lib/utils/chart-role-visibility.utils';

@Component({
  selector: 'app-stack-bar-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stack-bar-chart.component.html',
  styleUrls: ['./stack-bar-chart.component.scss']
})
export class StackBarChartComponent extends ParentStackBarChartComponent implements AfterViewInit, OnDestroy {
  private static readonly PRICE_DYNAMICS_LEGEND_EVENT = 'price-dynamics-legend-sync';
  private readonly onLegendSyncBound = (event: Event) => this.handleLegendSyncEvent(event as CustomEvent<any>);
  private activeLegendSelection: Set<string> | null = null;
  private currentRequestPayload: any;

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    window.addEventListener(StackBarChartComponent.PRICE_DYNAMICS_LEGEND_EVENT, this.onLegendSyncBound);
  }

  override ngOnDestroy(): void {
    window.removeEventListener(StackBarChartComponent.PRICE_DYNAMICS_LEGEND_EVENT, this.onLegendSyncBound);
    super.ngOnDestroy();
  }

  get showConnectorArrow(): boolean {
    const widgetId = String(this.data?.widgetId || '').toLowerCase();
    const title = String(this.data?.title || '').toLowerCase();
    return widgetId === 'widget-stack-3-1' || title.includes('cumulative price change');
  }

  protected override loadAndRenderChart(filters?: any): any {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (!chartDom) {
      console.warn('Chart DOM not found:', this.data?.widgetId);
      return;
    }

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }

    this.myChart.showLoading();

    if (this.data?.data) {
      this.initChart(this.data.data);
      this.addResizeListener();
      return;
    }

    if (!this.data?.apiUrl) {
      this.myChart.hideLoading();
      this.addResizeListener();
      return;
    }

    const dataForRequest = JSON.parse(JSON.stringify(this.data || {}));
    const requestParams = filters?.requestParams || {};
    const hasNestedFilterObject =
      !!dataForRequest?.customPayload?.requestData?.filter &&
      typeof dataForRequest.customPayload.requestData.filter === 'object';

    if (hasNestedFilterObject) {
      Object.entries(requestParams).forEach(([key, value]) => {
        const normalizedValue =
          Array.isArray(value) && value.length === 1 ? value[0] : value;
        dataForRequest.customPayload.requestData.filter[key] = normalizedValue;
      });
    }

    const filteredRequest = {
      ...(filters || {}),
      requestParams: hasNestedFilterObject ? {} : (filters?.requestParams || {}),
    };

    this.currentRequestPayload = dataForRequest;

    this.configService.getConfig(
      this.data.apiUrl,
      dataForRequest,
      filteredRequest,
      this.data.method
    ).subscribe({
      next: (res: any) => this.initChart(res?.data || res),
      error: () => this.myChart.hideLoading(),
    });

    this.addResizeListener();
  }

  protected override initChart(chartData: any): any {
    if (this.renderSingleCumulativeStackBar(chartData)) {
      return;
    }

    const dataKey = this.data?.dataKey;
    const isMissingData = !chartData;
    const isEmptySeriesArray =
      !!dataKey && Array.isArray(chartData?.[dataKey]) && chartData[dataKey].length === 0;

    // Prevent a transient empty refresh from blanking the chart UI.
    if (isMissingData || isEmptySeriesArray) {
      this.myChart?.hideLoading();
      return;
    }

    return super.initChart(chartData);
  }

  protected override setupChartClickEvent(): any {
    return super.setupChartClickEvent();
  }

  protected override updateFilterSelection(params: any): any {
    return super.updateFilterSelection(params);
  }

  protected override addResizeListener(): any {
    return super.addResizeListener();
  }

  private renderSingleCumulativeStackBar(chartData: any): boolean {
    const cumulativeSum =
      chartData?.cumulativeSum ??
      chartData?.responseList?.[0]?.responseData?.[0]?.cumulativeSum ??
      chartData?.responseList?.[0]?.data?.[0]?.cumulativeSum;

    if (!cumulativeSum || typeof cumulativeSum !== 'object') {
      return false;
    }

    const parseNumericValue = (rawValue: unknown): number | null => {
      if (rawValue === null || rawValue === undefined) {
        return null;
      }

      if (typeof rawValue === 'string') {
        const normalized = rawValue.trim().toLowerCase();
        if (
          normalized === '' ||
          normalized === 'null' ||
          normalized === 'undefined' ||
          normalized === 'na' ||
          normalized === 'n/a' ||
          normalized === '-'
        ) {
          return null;
        }
        const valueFromString = Number(normalized.replace(/,/g, ''));
        return Number.isFinite(valueFromString) ? valueFromString : null;
      }

      const numericValue = Number(rawValue);
      return Number.isFinite(numericValue) ? numericValue : null;
    };

    let entries = Object.entries(cumulativeSum)
      .map(([key, rawValue]) => ({ key, value: parseNumericValue(rawValue) }))
      .filter((item): item is { key: string; value: number } => item.value !== null)
      .filter((item) => Number(item.value.toFixed(1)) !== 0);

    if (entries.length === 0) {
      this.myChart?.hideLoading();
      return true;
    }

    const cphValueRaw = cumulativeSum.cphPctChange;
    const cphNumericValue = parseNumericValue(cphValueRaw);
    const cphHasData = cphNumericValue !== null && Number(cphNumericValue.toFixed(1)) !== 0;

    // In NACA context, when CPH has data, remove Rebuild entry (show only CPH)
    const { regionVal: visRegion, countryVal: visCountry } = this.resolvePriceDynamicsLegendContext();
    const isNacaContext = this.isNacaPriceDynamicsContext(visRegion, visCountry);
    if (isNacaContext && cphHasData) {
      entries = entries.filter(e => e.key !== 'rebuildPctChange');
    }

    const { cphLabel, rebuildLabel } = this.resolvePriceDynamicsLegendLabels(cphHasData);

    const labelMap: Record<string, string> = {
      constructionMachineryPct: 'Construction PPI %',
      miningMachineryPct: 'Mining PPI %',
      rebuildPctChange: rebuildLabel,
      cphPctChange: cphLabel,
      CAT: 'CAT % Change',
      MTU: 'MTU % Change',
    };

    const showGridLines = this.data?.chartConfig?.showGridLines === true;
    const colors: string[] = Array.isArray(this.data?.colors) ? this.data.colors : [];
    const colorKeyOrder = [
      'constructionMachineryPct',
      'miningMachineryPct',
      'rebuildPctChange',
      'cphPctChange',
      'CAT',
      'MTU',
    ];
    const colorByKey = colorKeyOrder.reduce((acc, key, index) => {
      if (colors[index]) {
        acc[key] = colors[index];
      }
      return acc;
    }, {} as Record<string, string>);
    const symbolByKey: Record<string, string> = {
      constructionMachineryPct: 'circle',
      miningMachineryPct: 'rect',
      rebuildPctChange: 'triangle',
      cphPctChange: 'diamond',
      CAT: 'roundRect',
      MTU: 'pin',
    };

    const getRepelOrder = (rank: number): number => {
      if (rank === 0) return 0;
      const step = Math.ceil(rank / 2);
      return rank % 2 === 1 ? -step : step;
    };

    const labelPlacementMap = new Map<number, { position: 'left' | 'right'; dx: number; dy: number }>();
    const labelGapStep = 14;
    const positiveBaseDy = -6;
    const negativeBaseDy = 6;

    const assignClusterPlacements = (
      cluster: Array<{ index: number; value: number }>,
      preferredSide: 'left' | 'right',
      baseDy: number,
    ) => {
      cluster.forEach((entry, rank) => {
        const side = rank % 2 === 0 ? preferredSide : (preferredSide === 'right' ? 'left' : 'right');
        const repelOrder = getRepelOrder(rank);
        const dy = Math.max(-48, Math.min(48, baseDy + repelOrder * labelGapStep));
        const dx = side === 'right' ? 8 : -8;
        labelPlacementMap.set(entry.index, { position: side, dx, dy });
      });
    };

    const positiveCluster = entries
      .map((item, index) => ({ index, value: item.value }))
      .filter((entry) => entry.value >= 0)
      .sort((a, b) => a.value - b.value);

    const negativeCluster = entries
      .map((item, index) => ({ index, value: item.value }))
      .filter((entry) => entry.value < 0)
      .sort((a, b) => a.value - b.value);

    assignClusterPlacements(positiveCluster, 'right', positiveBaseDy);
    assignClusterPlacements(negativeCluster, 'left', negativeBaseDy);

    const showMarkerValueLabels = this.shouldShowMarkerValueLabels();
    const series = entries.map((item, index) => {
      const resolvedColor = colorByKey[item.key] || colors[index] || '#3b82f6';
      return {
      name: labelMap[item.key] || item.key,
      type: 'scatter',
      symbol: symbolByKey[item.key] || 'circle',
      symbolSize: 15,
      labelLayout: {
        moveOverlap: 'shiftY',
        hideOverlap: true,
      },
      itemStyle: {
        color: resolvedColor,
      },
      label: {
        show: showMarkerValueLabels,
        position: labelPlacementMap.get(index)?.position ?? (item.value >= 0 ? 'right' : 'left'),
        offset: [
          labelPlacementMap.get(index)?.dx ?? (item.value >= 0 ? 8 : -8),
          labelPlacementMap.get(index)?.dy ?? (item.value >= 0 ? -12 : 12),
        ],
        formatter: ({ value }: { value: number[] }) => {
          const numericValue = Number(Array.isArray(value) ? value[1] : value ?? 0);
          if (!Number.isFinite(numericValue)) {
            return '';
          }
          const roundedValue = Number(numericValue.toFixed(1));
          return roundedValue === 0 ? '' : `${roundedValue.toFixed(1)}%`;
        },
        color: resolvedColor,
        fontWeight: 600,
        fontSize: 12,
      },
      emphasis: {
        focus: 'series',
      },
      data: [
        {
          value: [0, item.value],
          cumulativeKey: item.key,
        },
      ],
      z: 5,
    };
    });

    const tooltipFontFamily = '"Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif, "Segoe UI Symbol"';
    const tooltipTextStyle = {
      fontFamily: tooltipFontFamily,
      fontSize: this.data?.chartConfig?.legendFontSize ?? 10,
      fontWeight: 400,
      letterSpacing: '0',
      color: '#000000ff'
    };
    const isCumulativePriceChangeTooltip = this.showConnectorArrow;
    const tooltipValueSuffix = `${this.data?.chartConfig?.tooltipValueSuffix ?? '%'}`;
    const tooltipDecimalsRaw = Number(this.data?.chartConfig?.tooltipDecimals);
    const tooltipDecimals = Number.isFinite(tooltipDecimalsRaw) ? tooltipDecimalsRaw : 1;

    const option = {
      tooltip: {
        trigger: 'axis',
        appendToBody: true,
        axisPointer: { type: 'cross' },
        textStyle: tooltipTextStyle,
        extraCssText: `font-family:${tooltipFontFamily};`,
        ...(isCumulativePriceChangeTooltip
          ? {
              formatter: (params: any) => {
                const rows = Array.isArray(params) ? params : [params];
                return rows
                  .map((p: any) => {
                    const rawValue = p?.value?.value ?? p?.value ?? 0;
                    const raw = Array.isArray(rawValue) ? rawValue[1] : rawValue;
                    const numericValue = Number(raw);
                    const renderedValue = Number.isFinite(numericValue)
                      ? `${numericValue.toFixed(tooltipDecimals)}${tooltipValueSuffix}`
                      : `${raw ?? ''}`;
                    const rawColor = Array.isArray(p?.color) ? p.color[0] : p?.color;
                    const markerColor = typeof rawColor === 'string' ? rawColor : '#3b82f6';
                    return `
                      <div style="display:flex;align-items:center;gap:6px;line-height:1.2;">
                        <span style="width:10px;height:10px;border-radius:50%;background:${markerColor};display:inline-block;flex:0 0 10px;"></span>
                        <span style="font-family:${tooltipFontFamily};font-size:${this.data?.chartConfig?.legendFontSize ?? 10}px;font-weight:400;color:#000000ff;white-space:nowrap;">
                          ${p?.seriesName || ''}: ${renderedValue}
                        </span>
                      </div>
                    `;
                  })
                  .join('<br/>');
              },
            }
          : {
              formatter: (params: any) => {
                const rows = Array.isArray(params) ? params : [params];
                return rows
                  .map((p: any) => {
                    const rawValue = p?.value?.value ?? p?.value ?? 0;
                    const raw = Array.isArray(rawValue) ? rawValue[1] : rawValue;
                    const rounded = (Number(raw) || 0).toFixed(1);
                    const rawColor = Array.isArray(p?.color) ? p.color[0] : p?.color;
                    const markerColor = typeof rawColor === 'string' ? rawColor : '#3b82f6';
                    return `
                      <div style="display:flex;align-items:center;gap:6px;line-height:1.2;">
                        <span style="width:10px;height:10px;border-radius:50%;background:${markerColor};display:inline-block;flex:0 0 10px;"></span>
                        <span style="font-family:${tooltipFontFamily};font-size:${this.data?.chartConfig?.legendFontSize ?? 10}px;font-weight:400;color:#000000ff;white-space:nowrap;">
                          ${p?.seriesName || ''}: ${rounded}%
                        </span>
                      </div>
                    `;
                  })
                  .join('<br/>');
              }
            })
      },
      // legend: {
      //   data: series.map((s) => s.name),
      //   bottom: 0,
      //   type: 'scroll',
      // },
      grid: {
        top: '8%',
        left: '10%',
        right: '10%',
        bottom: '20%',
        containLabel: false,
      },
      xAxis: {
        type: 'value',
        min: -0.2,
        max: 0.2,
        show: true,
        position: 'bottom',
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: {
            width: 2,
            color: '#6b7280',
          },
        },
        axisTick: {
          show: true,
          length: 6,
          lineStyle: {
            width: 2,
            color: '#6b7280',
          },
        },
        axisLabel: {
          show: false,
        },
        splitNumber: 2,
        splitLine: {
          show: showGridLines,
        },
      },
      yAxis: {
        type: 'value',
        show: true,
        position: 'left',
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: {
            width: 2,
            color: '#6b7280',
          },
        },
        axisTick: {
          show: false,
          length: 6,
          lineStyle: {
            width: 2,
            color: '#6b7280',
          },
        },
        axisLabel: {
          show: false,
          fontSize: 14,
          fontWeight: 'bold',
          color: '#6b7280',
        },
        min: (value: { min: number }) => Math.min(0, Math.floor(value.min - 1)),
        max: (value: { max: number }) => Math.ceil(value.max + 1),
        splitLine: {
          show: showGridLines,
        },
      },
      series,
    };

    applyRoleVisibilityToOption(option, this.data);
    this.myChart?.setOption(option, true);
    this.applyLegendSelectionToMarkers();
    this.myChart?.hideLoading();
    this.setupChartClickEvent();
    return true;
  }

  private handleLegendSyncEvent(event: CustomEvent<any>): void {
    const detail = event?.detail;
    if (!detail || typeof detail !== 'object') return;

    const selectedMap = detail?.selectedMap;
    if (!selectedMap || typeof selectedMap !== 'object') {
      this.activeLegendSelection = null;
      this.applyLegendSelectionToMarkers();
      return;
    }

    const selectedNames = Object.keys(selectedMap).filter((name) => selectedMap[name] !== false);
    this.activeLegendSelection = new Set(selectedNames.map((name) => this.normalizeLegendName(name)));
    this.applyLegendSelectionToMarkers();
  }

  private applyLegendSelectionToMarkers(): void {
    if (!this.myChart) return;
    const option: any = this.myChart.getOption?.();
    const seriesList = Array.isArray(option?.series) ? option.series : [];
    if (seriesList.length === 0) return;

    const hasSelection = this.activeLegendSelection !== null;
    const selectedSet = this.activeLegendSelection || new Set<string>();
    const showMarkerValueLabels = this.shouldShowMarkerValueLabels();

    const updatedSeries = seriesList.map((series: any) => {
      if (series?.type !== 'scatter') return series;
      const normalizedSeriesName = this.normalizeLegendName(series?.name || '');
      const isVisible = !hasSelection || this.isLegendSeriesSelected(normalizedSeriesName, selectedSet);

      return {
        ...series,
        itemStyle: {
          ...(series.itemStyle || {}),
          opacity: isVisible ? 1 : 0,
        },
        label: {
          ...(series.label || {}),
          show: showMarkerValueLabels && isVisible,
        },
      };
    });

    this.myChart.setOption({ series: updatedSeries }, false);
  }

  private shouldShowMarkerValueLabels(): boolean {
    const chartConfig = this.data?.chartConfig || {};
    return this.data?.showLabels !== false &&
      chartConfig.showLabels !== false &&
      chartConfig.showDataLabels !== false &&
      chartConfig.showBarLabels !== false &&
      chartConfig.showLineLabels !== false;
  }

  private resolvePriceDynamicsLegendLabels(cphHasData: boolean): { cphLabel: string; rebuildLabel: string } {
    let cphLabel = 'CPH % Change';
    let rebuildLabel = 'Rebuild % Change';

    const { regionVal, engineRaw, mineName, countryVal } = this.resolvePriceDynamicsLegendContext();
    const engineVal = this.normalizeEngineLegendValue(engineRaw);

    if (this.isNacaPriceDynamicsContext(regionVal, countryVal)) {
      const nacaLegendBase = this.buildNacaLegendBase(regionVal, engineVal, mineName);

      if (nacaLegendBase) {
        rebuildLabel = nacaLegendBase;
        if (cphHasData && mineName) {
          cphLabel = `${mineName}-CPH`;
        }
      }

      return { cphLabel, rebuildLabel };
    }

    if (regionVal && engineVal) {
      rebuildLabel = engineVal;
    }

    return { cphLabel, rebuildLabel };
  }

  private buildNacaLegendBase(regionVal: string, engineVal: string, mineName: string): string {
    // Format engine value: replace commas with dashes (e.g. "QSK78,T2" -> "QSK78-T2")
    const formattedEngine = engineVal ? engineVal.replace(/,/g, '-') : '';
    const parts = [
      (regionVal || 'NACA').toUpperCase(),
      formattedEngine
    ].filter((value) => Boolean(String(value || '').trim()));
    if (parts.length > 0) {
      return parts.join('-').trim();
    }

    return String(mineName || '').trim();
  }

  private resolvePriceDynamicsLegendContext(): { regionVal: string; engineRaw: unknown; mineName: string; countryVal: string } {
    const liveFilter = this.data?.customPayload?.requestData?.filter || {};
    const payloadFilter = this.currentRequestPayload?.customPayload?.requestData?.filter || {};
    const state = history.state || {};
    const selectedMine = state?.selectedMine || {};
    const fullRow = selectedMine?.__fullRow || {};
    const primaryEngineFromRow = Array.isArray(fullRow?.engine_model)
      ? (fullRow.engine_model[0]?.engine_model_key || fullRow.engine_model[0]?.engine_model || '')
      : '';

    const regionVal = String(
      liveFilter.region ||
      payloadFilter.region ||
      state.selectedRegion ||
      state.region ||
      selectedMine.__regionRaw ||
      selectedMine.region ||
      fullRow.region ||
      '',
    ).trim();

    const engineRaw =
      liveFilter.engine_model ||
      liveFilter.engine_model_key ||
      payloadFilter.engine_model ||
      payloadFilter.engine_model_key ||
      state.selectedEngineWithoutEmission ||
      state.selectedEngineModelKey ||
      state.engine_model ||
      state.engine_model_key ||
      selectedMine.__engineModel ||
      fullRow.engine_model_key ||
      fullRow.engine_model ||
      primaryEngineFromRow ||
      '';

    const mineRaw =
      liveFilter.mine ||
      liveFilter.customer_name ||
      liveFilter.mine_name ||
      payloadFilter.mine ||
      payloadFilter.customer_name ||
      payloadFilter.mine_name ||
      state.selectedMineName ||
      state.mine_name ||
      state.customer_name ||
      selectedMine?.name ||
      selectedMine?.mine ||
      selectedMine?.__mineRaw ||
      fullRow.mine_site ||
      fullRow.mine ||
      fullRow.customer_name ||
      '';

    const mineName = typeof mineRaw === 'string'
      ? mineRaw.trim()
      : String(mineRaw?.name || mineRaw?.mine || mineRaw?.mine_site || '').trim();

    const countryVal = String(
      liveFilter.country ||
      payloadFilter.country ||
      state.country ||
      selectedMine?.__countryRaw ||
      selectedMine?.country ||
      fullRow.country ||
      fullRow.country_name ||
      '',
    ).trim();

    return { regionVal, engineRaw, mineName, countryVal };
  }

  private isNacaPriceDynamicsContext(regionVal: string, countryVal: string): boolean {
    if (regionVal.toUpperCase() === 'NACA') {
      return true;
    }

    const normalizedCountry = countryVal.trim().toUpperCase();
    return ['UNITED STATES', 'UNITED STATES OF AMERICA', 'USA', 'CANADA', 'MEXICO', 'UNITED MEXICAN STATES']
      .includes(normalizedCountry);
  }

  private normalizeEngineLegendValue(raw: unknown): string {
    if (!raw) return '';

    let engine = String(raw).trim();
    engine = engine.replace(/^([^,]+)-(T\d+).*/i, '$1,$2');
    engine = engine.replace(/^([^,]+,T\d+).*/i, '$1');
    return engine.trim();
  }

  private normalizeLegendName(name: string): string {
    return String(name || '')
      .replace(/%?\s*change$/i, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  }

  private isLegendSeriesSelected(seriesName: string, selectedNames: Set<string>): boolean {
    if (selectedNames.has(seriesName)) {
      return true;
    }

    return Array.from(selectedNames).some((selectedName) =>
      this.areLegendNamesEquivalent(seriesName, selectedName)
    );
  }

  private areLegendNamesEquivalent(nameA: string, nameB: string): boolean {
    if (nameA === nameB) {
      return true;
    }

    const suffixA = this.extractLegendSuffix(nameA);
    const suffixB = this.extractLegendSuffix(nameB);
    if (suffixA && suffixB && suffixA === suffixB) {
      return true;
    }

    return false;
  }

  private extractLegendSuffix(name: string): string {
    const normalized = String(name || '').trim();
    const parts = normalized.split(' - ');
    return parts.length > 1 ? parts[parts.length - 1].trim() : normalized;
  }
}
